/**
 * Resolves the Statsig user object from Center's identity model.
 *
 * Model:
 *   userID    = identity (the real authenticated person; randomization unit)
 *   customIDs = active subject + active org (changes when user switches subjects)
 *   custom    = display metadata + actingMode flag
 *
 * Two scenarios v1 supports:
 *   1. Self use         — identity has one subject; userID and subject align
 *   2. Subject switching — identity has many subjects; userID stays, subject changes
 *
 * Pre-auth (center-auth): userID falls back to anonymous session UUID.
 *
 * Admin impersonation is intentionally NOT special-cased here — events fired
 * during an impersonation session attribute to the admin's identity (which is
 * correct for "who acted") but the customIDs.subject reflects whatever the app
 * tells us is the active subject. If the app's getActiveSubject() returns the
 * impersonated user during impersonation, segmentation works naturally.
 */

/** Primitive types Statsig accepts in the user.custom bag. Mirrors the
 *  SDK's StatsigUserPrimitives so our user object passes typecheck against
 *  client.updateUserAsync without casts at every call site. */
export type StatsigCustomValue =
  | string
  | number
  | boolean
  | Array<string>
  | undefined
  | null;

export interface CenterStatsigUser {
  userID: string;
  /** Top-level display name — Statsig surfaces this in dashboard user lists.
   *  We do NOT forward email or other PII to Statsig (third-party SDK);
   *  see /standards/frontend/analytics.md. */
  name?: string;
  customIDs?: Record<string, string>;
  custom?: Record<string, StatsigCustomValue>;
}

export type ActingMode = 'self' | 'subject_switched';

export interface UserResolutionInput {
  /** From getTokenClaims; null when not authenticated. */
  tokenClaims: { sub?: string; name?: string; email?: string } | null;
  /** Anonymous fallback ID (session UUID) for pre-auth tracking. */
  anonymousId?: string;
  /** Active subject within the current org context. Null when none active. */
  activeSubjectId?: string | null;
  /** Active org context. */
  activeOrgId?: string | null;
  /** Whether the active subject differs from the identity's primary subject. */
  isSubjectSwitched?: boolean;
}

const ANONYMOUS_PREFIX = 'anon:';
const FALLBACK_USER_ID = 'anonymous';

export function resolveStatsigUser(input: UserResolutionInput): CenterStatsigUser {
  const isAuthenticated = input.tokenClaims != null;

  let userID: string;
  if (input.tokenClaims?.sub) {
    userID = input.tokenClaims.sub;
  } else if (input.anonymousId) {
    userID = `${ANONYMOUS_PREFIX}${input.anonymousId}`;
  } else {
    userID = FALLBACK_USER_ID;
    if (process.env['NODE_ENV'] !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[Statsig] Resolver fell back to literal "anonymous" — both tokenClaims ' +
          'and anonymousId were missing. This collapses every user with this ' +
          'state into one record. Check that getOrCreateSessionAnonymousId ' +
          'returns a string.'
      );
    }
  }

  const customIDs: Record<string, string> = {};
  if (input.activeSubjectId) customIDs['subject'] = input.activeSubjectId;
  if (input.activeOrgId) customIDs['org'] = input.activeOrgId;

  const actingMode: ActingMode = input.isSubjectSwitched ? 'subject_switched' : 'self';

  const custom: Record<string, StatsigCustomValue> = {
    isAuthenticated,
    actingMode,
    ...(input.activeSubjectId ? { activeSubjectId: input.activeSubjectId } : {}),
    ...(input.activeOrgId ? { activeOrgId: input.activeOrgId } : {}),
  };

  return {
    userID,
    // Display name only — surfaces in Statsig dashboard user lists. Email
    // and other PII are intentionally not forwarded (third-party SDK).
    ...(input.tokenClaims?.name ? { name: input.tokenClaims.name } : {}),
    ...(Object.keys(customIDs).length > 0 ? { customIDs } : {}),
    custom,
  };
}
